import React, { useEffect, useState } from 'react';
import { Upload, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { uploadFile } from '@/lib/storage';

export default function OrganizationTab() {
    const { profile } = useAuth();
    const [org, setOrg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!profile?.organization_id) return;
        supabase.from('organizations').select('*').eq('id', profile.organization_id).maybeSingle().then(({ data }) => setOrg(data));
    }, [profile?.organization_id]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { name, license_number, phone, email, address } = org;
            const { error } = await supabase.from('organizations').update({ name, license_number, phone, email, address }).eq('id', org.id);
            if (error) throw error;
            toast.success('Organization saved');
        } catch (err) { toast.error(err.message); } finally { setSaving(false); }
    };

    const onLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const { publicUrl } = await uploadFile({ folder: 'org-logo', file, organizationId: org.id });
            const { error } = await supabase.from('organizations').update({ logo_url: publicUrl }).eq('id', org.id);
            if (error) throw error;
            setOrg({ ...org, logo_url: publicUrl });
            toast.success('Logo updated');
        } catch (err) { toast.error(err.message); } finally { setUploading(false); }
    };

    const removeLogo = async () => {
        if (!window.confirm('Remove the organization logo?')) return;
        const { error } = await supabase.from('organizations').update({ logo_url: null }).eq('id', org.id);
        if (error) return toast.error(error.message);
        setOrg({ ...org, logo_url: null });
        toast.success('Logo removed');
    };

    if (!org) return <div className="space-y-3"><div className="aks-skeleton h-8 w-1/3" /><div className="aks-skeleton h-32" /><div className="aks-skeleton h-64" /></div>;

    const set = (k) => (e) => setOrg({ ...org, [k]: e.target.value });

    return (
        <div className="max-w-2xl space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="org-logo-section">
                <h2 className="text-lg font-bold text-aks-text mb-4">Organization logo</h2>
                <div className="flex items-center gap-5">
                    <div className="h-24 w-24 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {org.logo_url ? <img src={org.logo_url} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-8 w-8 text-gray-300" />}
                    </div>
                    <div className="flex-1">
                        <p className="text-[12px] text-gray-500 mb-3">PNG/JPG/SVG · square or wide. Used in dashboards, contracts and emails.</p>
                        <div className="flex flex-wrap gap-2">
                            <label className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px] cursor-pointer">
                                <Upload className="h-4 w-4" /> {uploading ? 'Uploading…' : (org.logo_url ? 'Replace logo' : 'Upload logo')}
                                <input type="file" accept="image/*" className="hidden" onChange={onLogoUpload} disabled={uploading} data-testid="org-logo-input" />
                            </label>
                            {org.logo_url && (
                                <button onClick={removeLogo} className="h-9 px-3 rounded-md border border-gray-300 hover:border-red-400 hover:text-red-600 text-[13px] font-semibold" data-testid="org-logo-remove">Remove</button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={save} className="bg-white border border-gray-200 rounded-xl p-6 space-y-4" data-testid="org-form">
                <h2 className="text-lg font-bold text-aks-text">Organization details</h2>
                <div>
                    <label className="text-[12px] font-medium">Brokerage name</label>
                    <input value={org.name || ''} onChange={set('name')} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-[12px] font-medium">License #</label>
                        <input value={org.license_number || ''} onChange={set('license_number')} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium">Phone</label>
                        <input value={org.phone || ''} onChange={set('phone')} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium">Email</label>
                        <input value={org.email || ''} onChange={set('email')} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium">Address</label>
                        <input value={org.address || ''} onChange={set('address')} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" />
                    </div>
                </div>
                <button disabled={saving} className="h-10 px-4 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px] disabled:opacity-60" data-testid="org-save-btn">{saving ? 'Saving…' : 'Save'}</button>
            </form>
        </div>
    );
}
